import { allPages } from "@/lib/pagination";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["owner", "admin", "developer", "viewer"];
const ROLE_DESC = {
  owner: "Full control incl. billing & org deletion",
  admin: "Manage projects, keys, rooms & members",
  developer: "Create & manage rooms, keys, projects",
  viewer: "Read-only access",
};

export default function Members() {
  const { orgId, currentOrg, loading } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("developer");

  const load = () => { if (orgId) allPages(`/orgs/${orgId}/members`).then((data) => setMembers(data)).catch(() => {}); };
  useEffect(load, [orgId]);

  const add = async () => {
    if (!orgId || loading) return;
    try { await api.post(`/orgs/${orgId}/members`, { email, role }); toast.success("Member added"); setOpen(false); setEmail(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeRole = async (userId, newRole) => {
    if (!orgId || loading) return;
    try { await api.patch(`/orgs/${orgId}/members/${userId}`, { role: newRole }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };
  const remove = async (userId) => {
    if (!orgId || loading) return;
    try { await api.delete(`/orgs/${orgId}/members/${userId}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="members-page">
      <PageHeader
        title="Members"
        description={`Role-based access for ${currentOrg?.name || "your org"}.`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={loading || !orgId} data-testid="add-member-button" className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200"><UserPlus className="mr-2 h-4 w-4" /> Add member</Button>
            </DialogTrigger>
            <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="add-member-dialog">
              <DialogHeader><DialogTitle className="text-zinc-100">Add member</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label className="text-zinc-300">Email (must have an account)</Label>
                  <Input data-testid="member-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-950 border-zinc-800" placeholder="teammate@company.com" /></div>
                <div className="space-y-2"><Label className="text-zinc-300">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="member-role-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-950">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">{ROLE_DESC[role]}</p>
                </div>
              </div>
              <DialogFooter><Button data-testid="add-member-confirm" disabled={!email || loading || !orgId} onClick={add} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="overflow-hidden rounded-md border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="px-4 py-3 font-medium">Member</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Role</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {members.map((m) => (
              <tr key={m.user_id} data-testid={`member-row-${m.email}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-800 text-xs font-medium text-zinc-200">{(m.name || m.email)[0]?.toUpperCase()}</div>
                    <span className="text-zinc-100">{m.name || "—"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400">{m.email}</td>
                <td className="px-4 py-3">
                  {m.role === "owner" ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-400"><ShieldCheck className="h-3.5 w-3.5" /> owner</span>
                  ) : (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                      <SelectTrigger data-testid={`member-role-${m.email}`} className="h-8 w-32 bg-zinc-950 border-zinc-800 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-950">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {m.role !== "owner" && (
                    <button data-testid={`remove-member-${m.email}`} onClick={() => remove(m.user_id)} className="rounded border border-red-900/50 p-1.5 text-red-400 hover:bg-red-950/40"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
